import { Bell, CircleCheck, Info, X } from "lucide-react";
import { runGroomingToastAction, useGroomingToastStore, type GroomingToastKind } from "@/lib/grooming/toasts";

const ICONS: Record<GroomingToastKind, typeof CircleCheck> = {
  success: CircleCheck,
  info: Info,
  notice: Bell,
};

const TOAST_CLASS =
  "pointer-events-auto flex max-w-[420px] animate-slide-up items-center gap-2.5 rounded-[10px] border border-[#27272A] bg-[#18181B] px-3.5 py-2.5 text-left text-[13px] text-foreground shadow-[0_12px_32px_rgba(0,0,0,.5)]";

/**
 * The grooming toast stack, bottom right (design `.toasts` / `.toast`).
 * Mounted once by the Toolbox frame and once by the browser layout on
 * grooming pages. Click a toast to dismiss it.
 *
 * A toast with an action (PKG-PM-C's Undo, frame C2) is not one big button:
 * it holds the message, the action's button and a small close button, so
 * the action can never be pressed by a click meant to dismiss.
 */
export function GroomingToasts() {
  const toasts = useGroomingToastStore((state) => state.toasts);
  const dismiss = useGroomingToastStore((state) => state.dismiss);

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2"
      aria-live="polite"
      data-testid="grooming-toasts"
    >
      {toasts.map((toast) => {
        const Icon = ICONS[toast.kind];
        const icon = <Icon className="h-[15px] w-[15px] flex-none text-[#4ADE80]" aria-hidden="true" />;
        if (toast.action) {
          return (
            <div key={toast.id} role="status" className={TOAST_CLASS} data-testid="grooming-toast">
              {icon}
              <span>{toast.message}</span>
              <button
                type="button"
                className="flex-none rounded-md px-1.5 py-0.5 text-[13px] font-semibold text-link hover:underline"
                onClick={() => {
                  runGroomingToastAction(toast.id);
                }}
                data-testid="grooming-toast-action"
              >
                {toast.action.label}
              </button>
              <button
                type="button"
                className="flex-none rounded-md p-0.5 text-muted-foreground hover:text-foreground"
                aria-label="Dismiss"
                onClick={() => {
                  dismiss(toast.id);
                }}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          );
        }
        return (
          <button
            key={toast.id}
            type="button"
            role="status"
            className={TOAST_CLASS}
            onClick={() => {
              dismiss(toast.id);
            }}
          >
            {icon}
            <span>{toast.message}</span>
          </button>
        );
      })}
    </div>
  );
}
